import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaSearch, FaBars, FaTimes } from "react-icons/fa";
import { useConfig } from '../../contexts/ConfigContext';

const Header = ({ totalItems = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { config } = useConfig();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleSearch = () => setIsSearchOpen(!isSearchOpen);

  // Función para ajustar el brillo del color
  const adjustColor = (hex, percent) => {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  };

  // Calcular colores derivados
  const primaryColor = config.colorPrincipalPublica;
  const darkerColor = adjustColor(primaryColor, -20);

  // Determinar si el color es claro u oscuro para el texto
  const isLightColor = (hex) => {
    if (!hex) return false;
    const rgb = parseInt(hex.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma > 150;
  };

  const textColor = isLightColor(primaryColor) ? '#000000' : '#ffffff';
  //const textColorSecondary = isLightColor(primaryColor) ? '#333333' : '#e5e5e5';

  return (
    <header className="w-full shadow-lg">
      {/* Barra superior con métodos de pago */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-2 text-sm">
          <div className="flex items-center space-x-3">
            <span className="text-gray-300 font-medium mr-3">Aceptamos:</span>
            <div className="flex items-center space-x-2 opacity-80 hover:opacity-100 transition-opacity">
              <img width="32" height="32" src="/visa.webp" alt="visa" className="hover:scale-110 transition-transform" />
              <img width="32" height="32" src="/mastercard.webp" alt="mastercard" className="hover:scale-110 transition-transform" />
              <img width="32" height="32" src="/maestro.webp" alt="maestro" className="hover:scale-110 transition-transform" />
              <img width="32" height="32" src="/amex.webp" alt="amex" className="hover:scale-110 transition-transform" />
            </div>
          </div>
        </div>
      </div>

      {/* Header principal con colores dinámicos */}
      <div
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${darkerColor} 100%)`,
          color: textColor
        }}
      >
        {/* Efecto de brillo sutil */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center group cursor-pointer">
            <div className="relative">
              <img
                width="150" height="20"
                src="/LOGO_imporisel.webp"
                alt="Imporisel Logo"
                className=" group-hover:scale-105 transition-transform duration-300"
              />

            </div>
          </Link>

          {/* Navegación desktop */}
          <nav className="hidden lg:flex items-center space-x-8">
            <ul className="flex items-center space-x-8 font-semibold text-lg">
              <li>
                <Link
                  to="/"
                  className="hover:text-yellow-400 transition-colors duration-300 relative group"
                >
                  Inicio
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
                </Link>
              </li>
              <li>
                <Link
                  to="/catalogo"
                  className="hover:text-yellow-400 transition-colors duration-300 relative group"
                >
                  Catálogo
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
                </Link>
              </li>
              {config.mostrarBlog && (
                <li>
                  <Link
                    to="/blog"
                    className="hover:text-yellow-400 transition-colors duration-300 relative group"
                  >
                    Blog
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
                  </Link>
                </li>
              )}
            </ul>

            {/* Iconos de acción */}
            <div className="flex items-center space-x-4 ml-6">
              {/* Búsqueda móvil */}
              <button
                onClick={toggleSearch}
                className="lg:hidden text-white hover:text-yellow-400 transition-colors duration-300"
              >
                <FaSearch className="w-5 h-5" />
              </button>

              {/* Login */}
              {/* <Link
                to="/login"
                className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 px-4 py-2 rounded-full transition-all duration-300 group"
                title="Iniciar sesión"
              >
                <FaSignInAlt className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="hidden xl:block text-sm font-medium">Acceder</span>
              </Link> */}

              {/* Carrito mejorado */}
              <button
                onClick={() => navigate("/ordenes")}
                className="relative flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-500 text-gray-800 px-4 py-2 rounded-full transition-all duration-300 group shadow-lg hover:shadow-xl"
              >
                <FaShoppingCart className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="hidden xl:block text-sm font-bold">Carrito</span>
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold animate-pulse">
                    {totalItems}
                  </span>
                )}
              </button>
            </div>
          </nav>

          {/* Menú hamburguesa móvil */}
          <button
            onClick={toggleMenu}
            className="lg:hidden text-white hover:text-yellow-400 transition-colors duration-300 z-50"
          >
            {isMenuOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
          </button>
        </div>

        {/* Barra de búsqueda móvil */}
        {isSearchOpen && (
          <div className="lg:hidden px-6 pb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar productos..."
                className="w-full px-4 py-3 pr-12 rounded-full text-gray-800 bg-white/95 backdrop-blur-sm focus:outline-none focus:bg-white transition-all duration-300"
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-yellow-400 hover:bg-yellow-500 text-gray-800 p-2 rounded-full transition-colors duration-300">
                <FaSearch className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Menú móvil desplegable */}
      <div className={`lg:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
        style={{ backgroundColor: darkerColor, color: textColor }}
      >
        <div className="px-6 py-4 space-y-4">
          <Link
            to="/"
            className="block py-3 px-4 hover:bg-white/10 rounded-lg transition-colors duration-300 font-semibold"
            onClick={() => setIsMenuOpen(false)}
          >
            Inicio
          </Link>
          <Link
            to="/catalogo"
            className="block py-3 px-4 hover:bg-white/10 rounded-lg transition-colors duration-300 font-semibold"
            onClick={() => setIsMenuOpen(false)}
          >
            Catálogo
          </Link>
          {config.mostrarBlog && (
            <Link
              to="/blog"
              className="block py-3 px-4 hover:bg-white/10 rounded-lg transition-colors duration-300 font-semibold"
              onClick={() => setIsMenuOpen(false)}
            >
              Blog
            </Link>
          )}

          <hr className="border-white/20 my-4" />

          {/* <Link
            to="/login"
            className="flex items-center space-x-3 py-3 px-4 hover:bg-white/10 rounded-lg transition-colors duration-300 font-semibold"
            onClick={() => setIsMenuOpen(false)}
          >
            <FaSignInAlt className="w-4 h-4" />
            <span>Iniciar Sesión</span>
          </Link> */}

          <button
            onClick={() => {
              navigate("/ordenes");
              setIsMenuOpen(false);
            }}
            className="flex items-center justify-between w-full py-3 px-4 hover:bg-white/10 rounded-lg transition-colors duration-300 font-semibold"
          >
            <div className="flex items-center space-x-3">
              <FaShoppingCart className="w-4 h-4" />
              <span>Mi Carrito</span>
            </div>
            {totalItems > 0 && (
              <span className="bg-yellow-400 text-gray-800 rounded-full px-2 py-1 text-xs font-bold">
                {totalItems}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;